import { MusicId } from "../../engine/musicId";
import { MusicLoader } from "./musicLoader";

const DEFAULT_FADE_MS = 5000;

// Loudness tunables — see docs/music.md.
const MUSIC_GLOBAL_BOOST = 0.6;
const MUSIC_TRACK_BOOST: Record<MusicId, number> = {
	[MusicId.WASTELAND]: 2.5,
	[MusicId.CAVES]: 1.0,
	[MusicId.ROBOT_BASE]: 1.0,
	[MusicId.BOSS_PLOW]: 1.0,
	[MusicId.BOSS_BLACK_HOLE]: 1.0,
};

interface Deck {
	audio: HTMLAudioElement;
	source: MediaElementAudioSourceNode;
	fadeGain: GainNode;
	boostGain: GainNode;
	trackId: MusicId | null;
}

/** Two-deck crossfading music player. See docs/music.md for the gain graph. */
export class MusicPlayer {
	private readonly audioContext: AudioContext;
	private readonly master: GainNode;
	private deckA: Deck;
	private deckB: Deck;
	private activeDeck: Deck;
	private currentTrackId: MusicId | null = null;
	private volume: number = 1.0;
	private fadeOutTimer: ReturnType<typeof setTimeout> | null = null;
	private fadeOutDeck: Deck | null = null;

	constructor(audioContext: AudioContext) {
		this.audioContext = audioContext;
		this.master = audioContext.createGain();
		this.master.gain.value = this.volume * MUSIC_GLOBAL_BOOST;
		this.master.connect(audioContext.destination);
		this.deckA = this.createDeck();
		this.deckB = this.createDeck();
		this.activeDeck = this.deckA;
	}

	private createDeck(): Deck {
		const audio = new Audio();
		audio.loop = true;
		audio.preload = "auto";
		audio.crossOrigin = "anonymous";
		const source = this.audioContext.createMediaElementSource(audio);
		const fadeGain = this.audioContext.createGain();
		fadeGain.gain.value = 0;
		const boostGain = this.audioContext.createGain();
		boostGain.gain.value = 1.0;
		source.connect(fadeGain);
		fadeGain.connect(boostGain);
		boostGain.connect(this.master);
		return { audio, source, fadeGain, boostGain, trackId: null };
	}

	setTrack(trackId: MusicId | null, fadeMs: number = DEFAULT_FADE_MS): void {
		if (this.currentTrackId === trackId) return;

		this.cancelPendingFadeOut();

		const oldDeck = this.activeDeck;
		const fadeSec = Math.max(0.05, fadeMs / 1000);
		const now = this.audioContext.currentTime;

		if (oldDeck.trackId !== null) {
			const currentGain = oldDeck.fadeGain.gain.value;
			oldDeck.fadeGain.gain.cancelScheduledValues(now);
			oldDeck.fadeGain.gain.setValueAtTime(currentGain, now);
			oldDeck.fadeGain.gain.linearRampToValueAtTime(0, now + fadeSec);
			this.fadeOutDeck = oldDeck;
			this.fadeOutTimer = setTimeout(() => this.finishFadeOut(), fadeMs);
		}

		if (trackId !== null) {
			const newDeck = oldDeck === this.deckA ? this.deckB : this.deckA;
			const url = MusicLoader.getUrlForMusicId(trackId);
			if (url === null) {
				this.currentTrackId = null;
				return;
			}
			newDeck.audio.src = url;
			newDeck.audio.currentTime = 0;
			newDeck.trackId = trackId;
			newDeck.boostGain.gain.cancelScheduledValues(now);
			newDeck.boostGain.gain.setValueAtTime(MUSIC_TRACK_BOOST[trackId], now);
			// Fade ramps 0..1; user/global gain lives on the master node.
			newDeck.fadeGain.gain.cancelScheduledValues(now);
			newDeck.fadeGain.gain.setValueAtTime(0, now);
			newDeck.fadeGain.gain.linearRampToValueAtTime(1, now + fadeSec);
			// Autoplay policy may reject this; tryStart() retries on user gesture.
			newDeck.audio.play().catch(() => {});
			this.activeDeck = newDeck;
		}
		this.currentTrackId = trackId;
	}

	private cancelPendingFadeOut(): void {
		if (this.fadeOutTimer !== null) {
			clearTimeout(this.fadeOutTimer);
			this.fadeOutTimer = null;
		}
		this.fadeOutDeck = null;
	}

	private finishFadeOut(): void {
		const deck = this.fadeOutDeck;
		this.fadeOutTimer = null;
		this.fadeOutDeck = null;
		if (deck === null) return;
		deck.audio.pause();
		// Leave audio.src loaded — clearing it triggers a network reset for no benefit.
		deck.trackId = null;
	}

	setVolume(level: number): void {
		this.volume = Math.max(0, Math.min(1, level));
		const now = this.audioContext.currentTime;
		this.master.gain.cancelScheduledValues(now);
		this.master.gain.setValueAtTime(this.volume * MUSIC_GLOBAL_BOOST, now);
	}

	/** Call on user gesture — retries play() if autoplay policy blocked the initial start. */
	tryStart(): void {
		this.audioContext.resume().catch(() => {});
		if (this.deckA.trackId !== null && this.deckA.audio.paused) {
			this.deckA.audio.play().catch(() => {});
		}
		if (this.deckB.trackId !== null && this.deckB.audio.paused) {
			this.deckB.audio.play().catch(() => {});
		}
	}

	pause(): void {
		this.deckA.audio.pause();
		this.deckB.audio.pause();
	}

	resume(): void {
		if (this.activeDeck.trackId !== null) {
			this.activeDeck.audio.play().catch(() => {});
		}
	}

	dispose(): void {
		this.cancelPendingFadeOut();
		this.deckA.audio.pause();
		this.deckB.audio.pause();
		this.deckA.fadeGain.disconnect();
		this.deckA.boostGain.disconnect();
		this.deckB.fadeGain.disconnect();
		this.deckB.boostGain.disconnect();
		this.deckA.source.disconnect();
		this.deckB.source.disconnect();
		this.master.disconnect();
		this.deckA.trackId = null;
		this.deckB.trackId = null;
		this.currentTrackId = null;
	}
}
